import { useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Camera, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useProfile, useUpdateProfile, useUpdatePassword } from '@/hooks/useAuth'
import { authApi } from '@/api/auth'
import { profileFormSchema, type ProfileFormData, passwordFormSchema, type PasswordFormData } from '@/schemas/account'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { showError } from '@/lib/error'

export function AccountPage() {
	const { data: user, isLoading: profileLoading } = useProfile()
	const updateProfileMutation = useUpdateProfile()
	const updatePasswordMutation = useUpdatePassword()

	// 基本信息表单
	const profileForm = useForm<ProfileFormData>({
		resolver: zodResolver(profileFormSchema),
		defaultValues: {
			nickname: '',
			phone: '',
			email: '',
			avatar: '',
		},
	})

	// 修改密码表单
	const passwordForm = useForm<PasswordFormData>({
		resolver: zodResolver(passwordFormSchema),
		defaultValues: {
			old_password: '',
			new_password: '',
			confirm_password: '',
		},
	})

	// 头像上传
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [uploadingAvatar, setUploadingAvatar] = useState(false)

	// 当用户数据加载完成后，填充表单
	const [formInitialized, setFormInitialized] = useState(false)
	if (user && !formInitialized) {
		profileForm.reset({
			nickname: user.nickname || '',
			phone: user.phone || '',
			email: user.email || '',
			avatar: user.avatar || '',
		})
		setFormInitialized(true)
	}

	// 头像上传处理
	const handleAvatarClick = () => {
		fileInputRef.current?.click()
	}

	const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) return

		// 校验文件大小 2MB
		if (file.size > 2 * 1024 * 1024) {
			toast.error('文件大小不能超过 2MB')
			return
		}

		// 校验文件类型
		if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
			toast.error('只支持 JPG/PNG 格式')
			return
		}

		setUploadingAvatar(true)
		try {
			const url = await authApi.uploadAvatar(file)
			profileForm.setValue('avatar', url)
			// 自动保存头像
			await updateProfileMutation.mutateAsync({ avatar: url })
			toast.success('头像更新成功')
		} catch (err) {
			showError(err, '头像上传失败')
		} finally {
			setUploadingAvatar(false)
		}
	}

	// 保存基本信息
	const onProfileSubmit = async (data: ProfileFormData) => {
		try {
			await updateProfileMutation.mutateAsync({
				nickname: data.nickname,
				phone: data.phone || undefined,
				avatar: data.avatar || undefined,
			})
			toast.success('资料更新成功')
		} catch (err) {
			showError(err, '更新失败')
		}
	}

	// 修改密码
	const onPasswordSubmit = async (data: PasswordFormData) => {
		try {
			await updatePasswordMutation.mutateAsync({
				old_password: data.old_password,
				new_password: data.new_password,
			})
			toast.success('密码修改成功')
			passwordForm.reset()
		} catch (err) {
			showError(err, '密码修改失败')
		}
	}

	if (profileLoading) {
		return (
			<div className="mx-auto max-w-2xl space-y-6">
				<div className="space-y-2">
					<div className="h-8 w-32 animate-pulse rounded bg-muted" />
					<div className="h-4 w-48 animate-pulse rounded bg-muted" />
				</div>
				<div className="h-96 animate-pulse rounded-lg bg-muted" />
			</div>
		)
	}

	const currentAvatar = profileForm.watch('avatar')

	return (
		<div className="mx-auto max-w-2xl space-y-6">
			{/* 页面标题 */}
			<div>
				<h1 className="text-2xl font-bold tracking-tight">账户设置</h1>
				<p className="text-muted-foreground">管理您的账户信息和安全设置</p>
			</div>

			{/* 基本信息 */}
			<Card>
				<CardHeader>
					<CardTitle>基本信息</CardTitle>
					<CardDescription>更新您的个人资料信息</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-6">
						{/* 头像上传 */}
						<div className="flex items-center gap-4">
							<div className="relative">
								<Avatar className="h-20 w-20 cursor-pointer" onClick={handleAvatarClick}>
									<AvatarImage src={currentAvatar} alt="头像" />
									<AvatarFallback className="text-xl bg-primary text-primary-foreground">
										{user?.username?.charAt(0).toUpperCase() || 'U'}
									</AvatarFallback>
								</Avatar>
								{uploadingAvatar && (
									<div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
										<Loader2 className="h-6 w-6 animate-spin text-white" />
									</div>
								)}
								{!uploadingAvatar && (
									<div className="absolute bottom-0 right-0 rounded-full bg-primary p-1">
										<Camera className="h-3 w-3 text-primary-foreground" />
									</div>
								)}
							</div>
							<div className="text-sm text-muted-foreground">
								<p>点击头像更换</p>
								<p>支持 JPG/PNG，最大 2MB</p>
							</div>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/jpeg,image/png"
								className="hidden"
								onChange={handleAvatarChange}
							/>
						</div>

						<Separator />

						{/* 昵称 */}
						<div className="space-y-2">
							<Label htmlFor="nickname">昵称</Label>
							<Input
								id="nickname"
								placeholder="请输入昵称"
								{...profileForm.register('nickname')}
							/>
							{profileForm.formState.errors.nickname && (
								<p className="text-sm text-destructive">{profileForm.formState.errors.nickname.message}</p>
							)}
						</div>

						{/* 手机号 */}
						<div className="space-y-2">
							<Label htmlFor="phone">手机号</Label>
							<Input
								id="phone"
								placeholder="请输入手机号"
								{...profileForm.register('phone')}
							/>
							{profileForm.formState.errors.phone && (
								<p className="text-sm text-destructive">{profileForm.formState.errors.phone.message}</p>
							)}
						</div>

						{/* 邮箱 */}
						<div className="space-y-2">
							<Label htmlFor="email">邮箱</Label>
							<Input
								id="email"
								type="email"
								placeholder="请输入邮箱"
								{...profileForm.register('email')}
							/>
							{profileForm.formState.errors.email && (
								<p className="text-sm text-destructive">{profileForm.formState.errors.email.message}</p>
							)}
						</div>

						<Button type="submit" disabled={updateProfileMutation.isPending}>
							{updateProfileMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
							保存
						</Button>
					</form>
				</CardContent>
			</Card>

			{/* 修改密码 */}
			<Card>
				<CardHeader>
					<CardTitle>修改密码</CardTitle>
					<CardDescription>定期更换密码有助于保护账户安全</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="old_password">当前密码</Label>
							<Input
								id="old_password"
								type="password"
								placeholder="请输入当前密码"
								{...passwordForm.register('old_password')}
							/>
							{passwordForm.formState.errors.old_password && (
								<p className="text-sm text-destructive">{passwordForm.formState.errors.old_password.message}</p>
							)}
						</div>

						<div className="space-y-2">
							<Label htmlFor="new_password">新密码</Label>
							<Input
								id="new_password"
								type="password"
								placeholder="请输入新密码（至少 6 位）"
								{...passwordForm.register('new_password')}
							/>
							{passwordForm.formState.errors.new_password && (
								<p className="text-sm text-destructive">{passwordForm.formState.errors.new_password.message}</p>
							)}
						</div>

						<div className="space-y-2">
							<Label htmlFor="confirm_password">确认新密码</Label>
							<Input
								id="confirm_password"
								type="password"
								placeholder="请再次输入新密码"
								{...passwordForm.register('confirm_password')}
							/>
							{passwordForm.formState.errors.confirm_password && (
								<p className="text-sm text-destructive">{passwordForm.formState.errors.confirm_password.message}</p>
							)}
						</div>

						<Button type="submit" disabled={updatePasswordMutation.isPending}>
							{updatePasswordMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
							修改密码
						</Button>
					</form>
				</CardContent>
			</Card>
		</div>
	)
}
